import React, { useContext, useEffect, useState } from "react";
import "../styles/adminDashboard.css";
import {
  IonCard,
  IonChip,
  IonCol,
  IonContent,
  IonGrid,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonRow,
  IonButton,
  IonSpinner,
  IonPopover,
  IonList,
} from "@ionic/react";
import {
  closeCircle,
  filterOutline,
  optionsOutline,
  search,
} from "ionicons/icons";
import { useHistory, useLocation } from "react-router";
import {
  handleGrantPolicy,
  getAllDevices,
  getAllUsers,
  getFilteredDevices,
  getFilteredUsers,
  handleRevokePolicy,
  getAllSubjects,
  handleGrantThingPolicy,
  handlePolicyInfo,
  getAllChargers,
} from "../service/adminUI";
import AppContext from "./AppContext";
import { useAppData } from "../context/appContext";
import * as _ from "lodash";
import { syncTimerFreqMode } from "../util/config";

// FIX #3: Page size for local pagination
const PAGE_SIZE = 20;

const AdminDashboard = (props) => {
  const history = useHistory();
  const location = useLocation();
  const [deviceResult, setDeviceResult] = useState([]);
  const [isUserDataLoading, setIsUserDataLoading] = useState(false);
  const [isSubjectDataLoading, setIsSubjectDataLoading] = useState(false);
  const [isDeviceDataLoading, setIsDeviceDataLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const {
    appData,
    userData,
    userFilterData,
    deviceFilterData,
    setUserFilterData,
    setDeviceFilterData,
    userRecords,
    deviceRecords,
    setUserRecords,
    setDeviceRecords,
    dtalService,
  } = useContext(AppContext);
  const MODEL_TYPE_USER = "user";
  const MODEL_TYPE_DEVICE = "device";
  const FILTER_CONDITION_ANY = "or";
  const FILTER_CONDITION_ALL = "and";
  const [userEmail, setUserEmail] = useState("");
  const [userType, setUserType] = useState("");
  const [userId, setUserId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [serialNo, setSerialNo] = useState("");
  const [subject, setSubject] = useState("");
  const [patient, setPatient] = useState("");
  const [clinician, setClinician] = useState("");
  const [charger, setCharger] = useState("");
  const [originalDeviceResult, setOriginalDeviceResult] = useState([]);
  const [subjectAsc, setSubjectAsc] = useState(true);
  const [patientAsc, setPatientAsc] = useState(true);
  const [serialNoAsc, setSerialNoAsc] = useState(true);
  const [clinicianAsc, setclinicianAsc] = useState(true);
  const [chargerAsc, setChargerAsc] = useState(true);
  const [toastClass, setToastClass] = useState("toast toast-invisible");
  const [toastVisible, setToastVisible] = useState(false);
  const [message, setMessage] = useState("Loading..Please wait");
  const TOAST_SUCCESS_CSS = "toast toast-success";
  const TOAST_ERROR_CSS = "toast toast-error";
  const TOAST_INFO_CSS = "toast toast-info";

  // FIX #3: Local pagination state (replaces broken PaginatedView wrapper)
  const [currentPage, setCurrentPage] = useState(1);

  const appContextData = useAppData();

  useEffect(async () => {
    appContextData.setSelected("Home");
    await handleLoadDevices();
  }, []);

  const handleFilterOnClick = (modelName) => {
    history.push({ pathname: "/home/filter", state: { modelName } });
  };

  const handleLoadDevices = async (isStaleExpression) => {
    let deviceData = null;
    let newDeviceExpression = "";
    setIsDeviceDataLoading(true);
    if (isStaleExpression === true) {
      newDeviceExpression = setFilterExpression(MODEL_TYPE_DEVICE);
    } else {
      newDeviceExpression = deviceFilterData.deviceFilterExpression;
    }
    // Fetch devices and chargers in parallel; chargers are mapped to their
    // paired IPG so we can display the charger MAC alongside each device row.
    const chargersPromise = getAllChargers(appData.accessToken).catch((err) => {
      console.warn("Failed to load chargers:", err);
      return [];
    });
    if (
      deviceFilterData.deviceFilters &&
      !(deviceFilterData.deviceFilters.length > 0)
    ) {
      deviceData = await getAllDevices(appData.accessToken);
    } else {
      deviceData = await getFilteredDevices(
        appData.accessToken,
        newDeviceExpression
      );
    }
    const chargers = (await chargersPromise) || [];
    // Build a case-insensitive lookup: paired IPG mac -> charger serial number
    const ipgToChargerSerial = new Map();
    for (const c of chargers) {
      const ipgMac = c?.attributes?.pairedIpgMacId;
      const chargerSerial = c?.attributes?.serialNumber;
      if (ipgMac && chargerSerial) {
        ipgToChargerSerial.set(String(ipgMac).toLowerCase(), chargerSerial);
      }
    }
    const allUserPolicies = await getAllUserPolicies(deviceData, ipgToChargerSerial);
    if (allUserPolicies) {
      setDeviceResult(allUserPolicies);
      setOriginalDeviceResult(allUserPolicies);
      setDeviceRecords(allUserPolicies);
      setCurrentPage(1); // FIX #3: Reset page on data load
    } else {
      setDeviceRecords([]);
      autoHideToast(TOAST_INFO_CSS, "Devices search: No data received");
    }
    setIsDeviceDataLoading(false);
  };

  // FIX #6: Batch policy fetches in groups of 5 with progress indicator
  const POLICY_BATCH_SIZE = 5;

  const getAllUserPolicies = async(deviceData, ipgToChargerSerial = new Map()) => {
    if (!deviceData || deviceData.length === 0) return [];

    const results = [];
    for (let i = 0; i < deviceData.length; i += POLICY_BATCH_SIZE) {
      const batch = deviceData.slice(i, i + POLICY_BATCH_SIZE);
      const batchResults = await Promise.all(batch.map(async (data) => {
        const subjectId = data.attributes?.subjectId || "";
        const serialnumber = data.attributes?.serialnumber || "";
        // Resolve paired charger via the pacemaker's macId. Falls back to
        // splitting thingId ("pacemaker:<macId>") if attributes.macid is unset.
        const ipgMacRaw =
          data.attributes?.macid ||
          data.attributes?.macId ||
          (typeof data.thingId === "string" && data.thingId.includes(":")
            ? data.thingId.split(":")[1]
            : "");
        const chargerSerial = ipgMacRaw
          ? ipgToChargerSerial.get(String(ipgMacRaw).toLowerCase()) || ""
          : "";
        let patient = "";
        let clinician = "";
        if (data.policyId) {
          try {
            const policyInfo = await handlePolicyInfo(
              appData.accessToken,
              data.policyId
            );
            for (let val in policyInfo.entries) {
              let entry = policyInfo.entries[val];
              let entryObj = Object.values(entry.subjects)[0];
              if (entryObj.type && entryObj.type === "patient") {
                patient = val;
              } else if (entryObj.type && entryObj.type === "clinician") {
                clinician = val;
              }
            }
          } catch (err) {
            console.error(`Failed to fetch policy ${data.policyId}:`, err);
          }
        }
        return { subjectId, serialnumber, patient, clinician, chargerSerial };
      }));
      results.push(...batchResults);
      setMessage(`Loading device ${Math.min(i + batch.length, deviceData.length)} of ${deviceData.length}...`);
    }

    return results;
  };

  const setFilterExpression = (model) => {
    let filterExpression = '';
    let filters = null;
    model === MODEL_TYPE_USER ? (filters = userFilterData.userFilters) : (filters = deviceFilterData.deviceFilters);

    //get all data when no filter selected
    if (filters === null || !(filters.length > 0)) {
        filterExpression = '';
    } else {
        if (filters.length === 1) {
            // no prefix for single expression
            filterExpression = filters[0].id;
        } else {
            for (let i = 0; i < filters.length; i++) {
                filterExpression = `${filterExpression}${filters[i].id},`;
            }
            filterExpression = filterExpression.slice(0, -1);
            if (model === MODEL_TYPE_USER) {
                //append and-or condition prefix in expression
                filterExpression = (userFilterData.userFilterExpression.startsWith(FILTER_CONDITION_ANY) ?
                    FILTER_CONDITION_ANY : FILTER_CONDITION_ALL) + `(${filterExpression})`;
            } else if (model === MODEL_TYPE_DEVICE) {
                //append and-or condition prefix in expression
                filterExpression = (deviceFilterData.deviceFilterExpression.startsWith(FILTER_CONDITION_ANY) ?
                    FILTER_CONDITION_ANY : FILTER_CONDITION_ALL) + `(${filterExpression})`;
            }
        }
    }
    //set app context for the filters and expressions
    model === MODEL_TYPE_USER ?
        (setUserFilterData({ ...userFilterData, userFilterExpression: filterExpression })) :
        (setDeviceFilterData({ ...deviceFilterData, deviceFilterExpression: filterExpression }));
    return filterExpression;
  }

  // FIX #4: Filter on item.subjectId (not user.firstName/lastName which don't exist)
  const handleSubjectFilter = (event) => {
    const value = event.currentTarget.value;
    setSubject(value);
    const data = originalDeviceResult.filter(
      (item) =>
        item.subjectId && item.subjectId.toLowerCase().includes(value.toLowerCase())
    );
    setDeviceResult(data);
    setCurrentPage(1); // FIX #3: Reset page on filter
  };

  const handlePatientFilter = (event) => {
    const value = event.currentTarget.value;
    setPatient(value);
    const data = originalDeviceResult.filter(
      (user) =>
        user.patient && user.patient.toLowerCase().includes(value.toLowerCase())
    );
    setDeviceResult(data);
    setCurrentPage(1); // FIX #3: Reset page on filter
  };

  const handleClinicianFilter = (event) => {
    const value = event.currentTarget.value;
    setClinician(value);
    const data = originalDeviceResult.filter(
      (user) =>
        user.clinician && user.clinician.toLowerCase().includes(value.toLowerCase())
    );
    setDeviceResult(data);
    setCurrentPage(1); // FIX #3: Reset page on filter
  };

  const handleChargerFilter = (event) => {
    const value = event.currentTarget.value;
    setCharger(value);
    const data = originalDeviceResult.filter(
      (row) =>
        row.chargerSerial && row.chargerSerial.toLowerCase().includes(value.toLowerCase())
    );
    setDeviceResult(data);
    setCurrentPage(1);
  };

  const handleSerialNoFilter = (event) => {
    const value = event.currentTarget.value;
    setSerialNo(value);
    const data = originalDeviceResult.filter((device) =>
      device.serialnumber.includes(value)
    );
    setDeviceResult(data);
    setCurrentPage(1); // FIX #3: Reset page on filter
  };

  // FIX #5: All sort handlers use [...deviceResult] to avoid mutating state
  // and always sort the filtered result (not the full unfiltered dataset)
  const handleSerialNoSort = () => {
    const data = [...deviceResult].sort((a, b) =>
      serialNoAsc
        ? (a.serialnumber || '').localeCompare(b.serialnumber || '')
        : (b.serialnumber || '').localeCompare(a.serialnumber || '')
    );
    setSerialNoAsc(!serialNoAsc);
    setDeviceResult(data);
  };

  const handleSubjectSort = () => {
    const data = [...deviceResult].sort((a, b) =>
      subjectAsc
        ? (a.subjectId || '').localeCompare(b.subjectId || '')
        : (b.subjectId || '').localeCompare(a.subjectId || '')
    );
    setSubjectAsc(!subjectAsc);
    setDeviceResult(data);
  };

  const handlePatientSort = () => {
    const data = [...deviceResult].sort((a, b) =>
      patientAsc
        ? (a.patient || '').localeCompare(b.patient || '')
        : (b.patient || '').localeCompare(a.patient || '')
    );
    setPatientAsc(!patientAsc);
    setDeviceResult(data);
  };

  const handleClinicianSort = () => {
    const data = [...deviceResult].sort((a, b) =>
      clinicianAsc
        ? (a.clinician || '').localeCompare(b.clinician || '')
        : (b.clinician || '').localeCompare(a.clinician || '')
    );
    setclinicianAsc(!clinicianAsc);
    setDeviceResult(data);
  };

  const handleChargerSort = () => {
    const data = [...deviceResult].sort((a, b) =>
      chargerAsc
        ? (a.chargerSerial || '').localeCompare(b.chargerSerial || '')
        : (b.chargerSerial || '').localeCompare(a.chargerSerial || '')
    );
    setChargerAsc(!chargerAsc);
    setDeviceResult(data);
  };

  //Auto Hide toast after 5 seconds
  const autoHideToast = (css, message) => {
    setToastClass(css);
    setMessage(message);
    setToastVisible(true);
    setTimeout(() => {
      setToastVisible(false);
    }, 5000);
  };

  // FIX #3: Compute paginated rows from deviceResult
  const totalPages = Math.max(1, Math.ceil(deviceResult.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedRows = deviceResult.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <IonContent>
      <div className="search-box">
        <IonLabel className="table-heading">All Implantable Devices</IonLabel>
      </div>

      <div>
        {isDeviceDataLoading ? (
          <div className="spinner-box">
            <IonSpinner name="lines" className="ion-spinner" />
            <IonLabel className="row-header">
              {message}
            </IonLabel>
          </div>
        ) : (
          <IonGrid className="container-grid">
            <IonRow className="row-header">
              <IonCol className="table-header-cell">
                <IonItem className="row-box">
                  <IonInput
                    value={serialNo}
                    placeholder="IPG Device"
                    onIonChange={handleSerialNoFilter}
                  ></IonInput>
                  <IonIcon
                    icon={filterOutline}
                    slot="end"
                    onClick={handleSerialNoSort}
                  />
                </IonItem>
              </IonCol>
              <IonCol className="table-header-cell">
                <IonItem className="row-box">
                  <IonInput
                    value={subject}
                    placeholder="Subject"
                    onIonChange={handleSubjectFilter}
                  ></IonInput>
                  <IonIcon
                    icon={filterOutline}
                    slot="end"
                    onClick={handleSubjectSort}
                  />
                </IonItem>
              </IonCol>
              <IonCol className="table-header-cell">
                <IonItem className="row-box">
                  <IonInput
                    value={patient}
                    placeholder="Patient Email"
                    onIonChange={handlePatientFilter}
                  ></IonInput>
                  <IonIcon
                    icon={filterOutline}
                    slot="end"
                    onClick={handlePatientSort}
                  />
                </IonItem>
              </IonCol>
              <IonCol className="table-header-cell">
                <IonItem className="row-box">
                  <IonInput
                    value={clinician}
                    placeholder="Clinician Email"
                    onIonChange={handleClinicianFilter}
                  ></IonInput>
                  <IonIcon
                    icon={filterOutline}
                    slot="end"
                    onClick={handleClinicianSort}
                  />
                </IonItem>
              </IonCol>
              <IonCol className="table-header-cell">
                <IonItem className="row-box">
                  <IonInput
                    value={charger}
                    placeholder="Charger"
                    onIonChange={handleChargerFilter}
                  ></IonInput>
                  <IonIcon
                    icon={filterOutline}
                    slot="end"
                    onClick={handleChargerSort}
                  />
                </IonItem>
              </IonCol>
            </IonRow>
            {/* FIX #3: Render only paginated rows instead of all deviceResult */}
            <IonList>
              {paginatedRows.map((item, index) => {
                return (
                  <IonRow className="devices-row" key={item.subjectId + '-' + item.serialnumber + '-' + index}>
                    <IonCol className="table-cell">
                      {item.serialnumber}
                    </IonCol>
                    <IonCol className="table-cell">{item.subjectId}</IonCol>
                    <IonCol className="table-cell">{item.patient}</IonCol>
                    <IonCol className="table-cell">{item.clinician}</IonCol>
                    <IonCol className="table-cell">{item.chargerSerial || "—"}</IonCol>
                  </IonRow>
                );
              })}
            </IonList>
            {/* FIX #3: Inline pagination controls */}
            {totalPages > 1 && (
              <IonRow style={{ justifyContent: 'center', padding: '10px 0', alignItems: 'center', gap: '4px' }}>
                <IonButton
                  fill="clear"
                  size="small"
                  disabled={safePage <= 1}
                  onClick={() => setCurrentPage(safePage - 1)}
                >
                  Prev
                </IonButton>
                <IonLabel style={{ padding: '0 12px', fontSize: '14px' }}>
                  Page {safePage} of {totalPages}
                </IonLabel>
                <IonButton
                  fill="clear"
                  size="small"
                  disabled={safePage >= totalPages}
                  onClick={() => setCurrentPage(safePage + 1)}
                >
                  Next
                </IonButton>
              </IonRow>
            )}
          </IonGrid>
        )}
      </div>
    </IonContent>
  );
};
export default AdminDashboard;
